import { z } from 'zod';
import { adapterClient, handleApiError } from './client';
import { canonicalProjectId } from './projectViews';
import { entityIdSchema } from '@/types/current-project';

export const submissionCommentSchema = z.object({
  cursor: z.number().int().positive().safe(),
  commentId: entityIdSchema,
  submissionId: entityIdSchema,
  authorAccount: z.string().regex(/^0x[0-9a-f]{64}$/),
  createdAt: z.number().int().nonnegative().safe(),
  message: z.string(),
  kind: z.enum(['Comment', 'Rejection']),
});
export type SubmissionComment = z.infer<typeof submissionCommentSchema>;
export const submissionCommentRequestSchema = submissionCommentSchema
  .pick({ commentId: true, message: true, kind: true })
  .extend({
    message: z
      .string()
      .refine(
        (s) =>
          !!s.trim() &&
          new TextEncoder().encode(s).length <= 4000 &&
          !s.includes('\0'),
        'Enter a comment up to 4000 UTF-8 bytes.'
      ),
  })
  .strict();
const path = (id: string) =>
  '/completion-submissions/' + canonicalProjectId(id) + '/comments';
export async function getSubmissionComments(id: string) {
  try {
    const items: SubmissionComment[] = [];
    let after = 0;
    for (;;) {
      const { data } = await adapterClient.get<unknown>(path(id), {
        params: { after },
      });
      const page = z
        .object({
          items: z.array(submissionCommentSchema),
          nextAfter: z.number().int().positive().safe().nullable(),
        })
        .parse(data);
      items.push(...page.items);
      if (page.nextAfter === null) return items;
      if (page.nextAfter <= after)
        throw new Error(
          'Invalid comment cursor. Retry loading this discussion.'
        );
      after = page.nextAfter;
    }
  } catch (e) {
    handleApiError(e, 'load delivery comments');
  }
}
export async function postSubmissionComment(
  id: string,
  request: z.infer<typeof submissionCommentRequestSchema>
) {
  try {
    const { data } = await adapterClient.post<unknown>(
      path(id),
      submissionCommentRequestSchema.parse(request)
    );
    return submissionCommentSchema.parse(data);
  } catch (e) {
    handleApiError(e, 'save delivery comment');
  }
}
